import { Injectable } from '@angular/core';
import { FormControl } from '@angular/forms';
import { NavigationEnd, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import moment, { Moment } from 'moment';
import { filter } from 'rxjs/operators';
import { AuthService } from '../shared/auth.service';
import { Globals } from '../shared/Globals';
import { TimeApprovalRequest, TimeTrackingService, TrackedStatus } from '../shared/time-tracking.service';

// One week, one person, one approval request, shared by the Hours and Km pages.
// Root-provided: the pages are siblings under the single app outlet. State resets
// when navigation lands outside /registration (urlAfterRedirects, so an old
// /timesheet?week= bookmark keeps the week Hours just read from it).
@Injectable({ providedIn: 'root' })
export class RegistrationWeekService {
  // Bumped on every context change (date, user, reset) so load() and refreshTrips()
  // can tell a response belongs to the context that is still current and drop it otherwise.
  private gen = 0;
  private _date: Moment = moment();
  readonly user = new FormControl<any>(null);
  request: TimeApprovalRequest | null = null;
  hasHours = false;
  tripRows = 0;
  // Counter, not a boolean: Hours' page(), Km's reload() and the trip widget's own
  // fetch each wrap their call in beginLoad()/endLoad(), so one caller finishing
  // first (e.g. a stale Hours fetch resolving after the user already moved to Km)
  // never re-enables the approval strip while another caller is still in flight.
  private pending = 0;
  get loading(): boolean { return this.pending > 0; }
  beginLoad(): void { this.pending++; }
  endLoad(): void { this.pending = Math.max(0, this.pending - 1); }

  constructor(private auth: AuthService, private api: TimeTrackingService,
              private translate: TranslateService, router: Router) {
    router.events.pipe(filter(e => e instanceof NavigationEnd))
      .subscribe((e: NavigationEnd) => { if (!e.urlAfterRedirects.startsWith('/registration')) { this.reset(); } });
    this.user.valueChanges.subscribe(() => { this.gen++; });
  }

  get date(): Moment { return this._date; }
  set date(d: Moment) { this._date = d; this.gen++; }

  // Read-only generation token: callers that commit their own state outside this
  // service (Hours' page()) capture this before a fetch and compare after, the same
  // way load()/refreshTrips() already do internally.
  get token(): number { return this.gen; }

  get weekStart(): string { return this.date.clone().startOf('isoWeek').format('YYYY-MM-DD'); }
  // Picking yourself in the member picker is your own week, not "other" (Codex I1):
  // an admin who selects their own account must stay fully editable.
  get viewingOther(): boolean {
    const id = this.user.value?.id;
    return !!id && id !== this.auth.user.getValue()?.id;
  }

  // Trips (#874): your own, or an Admin reading someone else's.
  get tripsAllowed(): boolean {
    return !!this.auth.user.getValue()?.trips_enabled && (!this.viewingOther || this.auth.isAdmin);
  }

  // An open trip row on the Km page (WeekTripsComponent.editing): Submit would send the week without it.
  editing = false;

  // Trip writes queued or in flight on the Km page (WeekTripsComponent.run). A km cell saves on
  // blur, so clicking Submit right after typing starts both requests; submit() waits for these first.
  private writes = new Set<Promise<unknown>>();
  trackWrite(write: Promise<unknown>): void {
    this.writes.add(write);
    write.catch(() => null).then(() => this.writes.delete(write));
  }

  get locked(): boolean {
    return !!this.request && [TrackedStatus.Submitted, TrackedStatus.Approved].includes(this.request.status);
  }

  get canSubmit(): boolean {
    return !this.locked && !this.editing && (this.hasHours || (this.tripsAllowed && this.tripRows > 0));
  }

  // Owner may withdraw their own still-pending submission (backend enforces ownership).
  get canRecall(): boolean { return this.request?.status === TrackedStatus.Submitted && !this.viewingOther; }
  get canReopen(): boolean {
    return this.request?.status === TrackedStatus.Approved && (this.auth.isAdmin || this.auth.isStaffer);
  }

  async load(): Promise<void> {
    const token = this.gen;
    const week = await this.api.getWeek(this.weekStart, TrackedStatus.Complete, this.user.value?.id || null).catch(() => null);
    if (!week || token !== this.gen) { return; } // superseded by a newer context change
    this.request = week.approval_request || null;
    this.hasHours = (week.trackings || []).some(t => (t.duration || 0) > 0);
  }

  // Raw fetch, no commit: lets a caller that needs to commit trips atomically with
  // something else (Hours' page(), alongside getWeek) decide for itself whether and
  // when to write tripRows, instead of refreshTrips() writing it unconditionally.
  async fetchTrips(weekStart: string): Promise<{rows: any[]} | null> {
    if (!this.tripsAllowed) { return {rows: []}; }
    return this.api.getTrips(weekStart, this.user.value?.id || null).catch(() => null);
  }

  async refreshTrips(weekStart: string = this.weekStart): Promise<void> {
    const token = this.gen;
    const trips = await this.fetchTrips(weekStart);
    if (token !== this.gen) { return; } // superseded by a newer context change
    this.tripRows = trips?.rows?.length ?? 0;
  }

  async submit(): Promise<void> {
    if (!this.canSubmit) { return; }
    const token = this.gen;
    while (this.writes.size) { await Promise.allSettled([...this.writes]); }
    if (token !== this.gen || !this.canSubmit) { return; }
    const res = await this.api.submitWeek(Globals.toISODate(this.date.toDate()), this.user.value?.id || null);
    if (res?.request) { this.request = res.request; }
  }

  async recall(): Promise<void> {
    if (!this.canRecall) { return; }
    this.request = await this.api.recallRequest(this.request.id);
  }

  async reopen(reason: string): Promise<void> {
    if (!this.canReopen || !reason) { return; }
    this.request = await this.api.reopenRequest(this.request.id, reason, this.translate.currentLang);
  }

  reset(): void {
    this.date = moment();   // bumps gen; the user reset below does not emit
    this.user.setValue(null, { emitEvent: false });
    this.request = null;
    this.hasHours = false;
    this.tripRows = 0;
  }
}
